import { CreateEntireVideoInstanceArgs, VideoUpload } from '@sparemin/api-sdk';

import {
  createEntireVideoInstance,
  searchEntireVideo,
} from 'redux/middleware/api/media-upload-service/actions';
import {
  entireVideoInstancesSelector,
  entireVideoSelector,
  reusableEntireVideoSelector,
} from 'redux/modules/entities/selectors';
import {
  uploadVideoToStorage,
  videoUploadOptionsSelector,
} from 'redux/modules/video-upload';
import { ThunkAction } from 'redux/types';
import { isFileLike, md5File } from 'utils/file';
import { getMediaImportedProviderArgs } from 'utils/mediaImport';

import { UploadEntireVideoOpts, UploadEntireVideoWaitFor } from './types';
import WaveformGenerationError from './WaveformGenerationError';

type UploadOpts = Omit<UploadEntireVideoOpts, 'src' | 'mediaImportedArgs'>;

// Single hash entry point: callers feed the result into both the entire-video
// search (for cache reuse) and `uploadVideoToStorage` (whose own video-cache
// search would otherwise re-hash the same bytes).
export async function hashLocalFile(
  src: File | string | undefined,
): Promise<string | undefined> {
  return isFileLike(src) ? md5File(src) : undefined;
}

// Searches the entire-video cache by md5 and returns the id of the first
// reusable entry (see `reusableEntireVideoSelector`). A hit may still be
// pending, so the caller polls it by id until it settles.
export const findReusableEntireVideo = (
  md5: string,
  waitFor: UploadEntireVideoWaitFor,
): ThunkAction<Promise<number | undefined>> => async (dispatch, getState) => {
  const { response } = await dispatch(searchEntireVideo({ md5 }));

  return reusableEntireVideoSelector(
    getState(),
    response?.result ?? [],
    waitFor,
  );
};

// Drives `uploadVideoToStorage` to populate the `VideoUpload` (`video`)
// entity. When a cached video entry exists, that action short-circuits via
// its own md5-keyed search — no bytes are re-uploaded.
const uploadVideoUploadEntity = (
  src: File,
  md5: string | undefined,
  uploadOpts: UploadOpts,
): ThunkAction<Promise<VideoUpload | undefined>> => async dispatch => {
  const videoEntity = await dispatch(
    uploadVideoToStorage({ src, md5, ...uploadOpts }, undefined, {
      waitForUpload: true,
    }),
  );

  return videoEntity?.toJS();
};

// Cache-miss path: prepare the upload args (local file vs. imported media),
// then create the entire-video instance the poller will track.
export const createEntireVideoForUpload = ({
  src,
  md5,
  uploadOpts,
  mediaImportedArgs,
}: {
  src: File | string | undefined;
  md5: string | undefined;
  uploadOpts: UploadOpts;
  mediaImportedArgs: UploadEntireVideoOpts['mediaImportedArgs'];
}): ThunkAction<Promise<{
  entireVideoInstanceId: number;
  entireUploadedVideo: VideoUpload | undefined;
}>> => async (dispatch, getState) => {
  let createArgs: CreateEntireVideoInstanceArgs | undefined;
  let entireUploadedVideo: VideoUpload | undefined;

  if (src && typeof src !== 'string') {
    entireUploadedVideo = await dispatch(
      uploadVideoUploadEntity(src, md5, uploadOpts),
    );

    const videoUploadOptions = videoUploadOptionsSelector(getState());

    // A fresh byte upload yields a direct-upload bucket/key; a video-cache hit
    // returns the existing upload by id with no bucket/key. Reference whichever
    // the upload produced so the instance request is never empty.
    createArgs =
      videoUploadOptions?.directUploadBucket &&
      videoUploadOptions?.directUploadKey
        ? {
            directUploadBucket: videoUploadOptions.directUploadBucket,
            directUploadKey: videoUploadOptions.directUploadKey,
          }
        : { uploadedVideoId: entireUploadedVideo?.id };
  }

  if (mediaImportedArgs) {
    createArgs = getMediaImportedProviderArgs(mediaImportedArgs);
  }

  const { response } = await dispatch(createEntireVideoInstance(createArgs));

  return { entireVideoInstanceId: response.id, entireUploadedVideo };
};

// Cache-hit path: hydrate the `VideoUpload` entity so downstream consumers
// (e.g. project creation) see the same state shape as the cache-miss path.
export const hydrateVideoUploadOnHit = (
  src: File,
  md5: string,
  uploadOpts: UploadOpts,
): ThunkAction<Promise<VideoUpload | undefined>> => dispatch =>
  dispatch(uploadVideoUploadEntity(src, md5, uploadOpts));

// Reads the `entireVideoId` produced by an entire-video instance, or
// `undefined` while the instance is still resolving one.
export const readInstanceEntireVideoId = (
  state: any,
  entireVideoInstanceId: number,
): number | undefined =>
  entireVideoInstancesSelector(state)?.getIn([
    String(entireVideoInstanceId),
    'entireVideo',
  ]) as number | undefined;

// Selects whether a poller should run again for the given entire-video,
// throwing `WaveformGenerationError` once a terminal failure is observed. A
// missing id or entity means the upload hasn't produced one yet — keep polling.
export const shouldKeepPollingEntireVideoSelector = (
  state: any,
  entireVideoId: number | undefined,
  waitFor: UploadEntireVideoWaitFor,
): boolean => {
  if (entireVideoId == null) {
    return true;
  }

  const entireVideo = entireVideoSelector(state, entireVideoId);

  if (entireVideo == null) {
    return true;
  }

  if (entireVideo.get('status') === 'failed') {
    throw new WaveformGenerationError();
  }

  if (waitFor === 'entireVideo') {
    return entireVideo.get('status') !== 'completed';
  }

  const waveformStatus = entireVideo.getIn(['waveform', 'status']);

  if (waveformStatus === 'failed' || waveformStatus === 'failedAcked') {
    throw new WaveformGenerationError();
  }

  return waveformStatus !== 'completed';
};
